import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import { animateCount } from '~/lib/animate-count'
import { currencyCaption } from '~/lib/currency-caption'
import { type Flyer, planFlyers } from '~/lib/flying-currency'
import { m } from '~/paraglide/messages'
import type {
  CurrencyKind,
  CurrencyProduct,
} from '~/services/currency-catalogue'
import type { ShopBalances } from '~/store/session-ledger'
import type { RemoteArtworkStatus } from '~/lib/remote-image'
import { createSignal, For, onCleanup, Show, type Accessor } from 'solid-js'
import { CurrencyArtwork, type CurrencyArtworkStatus } from './currency-artwork'
import { RemoteArtwork } from './remote-artwork'
import { RewardRevealScaffold } from './reward-reveal-scaffold'

/** A currency's built-in icon. */
export const currencyPresentation = {
  coin: { icon: coinIcon },
  gem: { icon: gemIcon },
} as const satisfies Record<CurrencyKind, { icon: string }>

// After the last coin lands, the settled number gets a beat to read before
// arrival is reported.
const ARRIVAL_SETTLE_MS = 250

/** One currency grant a Claim flies to its balance pill. */
export interface CurrencyGrant {
  amount: number
  currency: CurrencyKind
}

/**
 * The Reward reveal's Claim flight (GLOSSARY.md "Reward reveal"): coins or
 * gems burst from the revealed reward up to their balance pill, the pill
 * counts up from the first landing, and arrival is reported once the last
 * one lands and the settled number has had its beat. Only the display
 * animates — the caller credits the ledger on arrival. One flight at a time;
 * a reveal that presents several grants launches the next once the previous
 * has arrived.
 */
export interface CurrencyFlight {
  coinPillRef: (element: HTMLDivElement) => void
  /** The balance each pill shows: the counted value while one climbs. */
  displayedBalance: (currency: CurrencyKind) => number
  /** True from launch until the flight's arrival has been reported. */
  flying: Accessor<boolean>
  gemPillRef: (element: HTMLDivElement) => void
  launch: (grant: CurrencyGrant) => void
  rewardAnchorRef: (element: HTMLDivElement) => void
  // Read by CurrencyFlightLayer, which renders the burst.
  finishFlyer: (event: AnimationEvent) => void
  flyers: Accessor<Flyer[]>
  grant: Accessor<CurrencyGrant | undefined>
  interruptFlight: (event: AnimationEvent) => void
}

export function createCurrencyFlight(input: {
  balances: Accessor<ShopBalances | undefined>
  // Once per launch, after the settle beat: the caller credits the grant.
  onArrival: (grant: CurrencyGrant) => void
}): CurrencyFlight {
  const [grant, setGrant] = createSignal<CurrencyGrant>()
  // Coins/gems that fly from the revealed reward up to the balance pills.
  const [flyers, setFlyers] = createSignal<Flyer[]>([])
  // Once the first coin of the burst touches the pill, the granted
  // currency's pill counts up to the credited value (MONEY-1662).
  const [countedValue, setCountedValue] = createSignal<number>()
  let rewardElement: HTMLDivElement | undefined
  let coinBalanceElement: HTMLDivElement | undefined
  let gemBalanceElement: HTMLDivElement | undefined
  // Landings dedupe by element: a real flyer fires animationend once, but a
  // duplicate event on the same flyer must not count as a second landing.
  let landed = new Set<EventTarget>()
  let arrived = false
  let interrupted = false
  let countFrom = 0
  let countTarget = 0
  let countWindowMs = 0
  let cancelCount = () => {}
  let settleTimer = 0
  onCleanup(() => {
    cancelCount()
    window.clearTimeout(settleTimer)
  })

  const displayedBalance = (currency: CurrencyKind) => {
    const base = input.balances()?.[currency] ?? 0
    return currency === grant()?.currency ? (countedValue() ?? base) : base
  }

  // Reports arrival, then readies the flight for the next grant. The caller
  // credits the ledger on arrival, before the counted value is dropped, so
  // the pill never dips back to the pre-credit balance.
  const arrive = () => {
    const landedGrant = grant()
    if (landedGrant) input.onArrival(landedGrant)
    setGrant(undefined)
    setFlyers([])
    setCountedValue(undefined)
  }

  // Bump the pill via the Web Animations API (referral's reward reveal) — one
  // pop per landing coin makes the pill jitter as the staggered burst
  // arrives; cancelling the previous pop restarts it cleanly from scale 1.
  let popAnimation: Animation | undefined
  const popPill = () => {
    const element =
      grant()?.currency === 'coin' ? coinBalanceElement : gemBalanceElement
    popAnimation?.cancel()
    popAnimation =
      element?.animate?.(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.2)', offset: 0.4 },
          { transform: 'scale(1)' },
        ],
        { duration: 320, easing: 'ease-out' },
      ) ?? undefined
  }

  const launch = (next: CurrencyGrant) => {
    if (grant()) return
    const toRect = (element: HTMLDivElement | undefined) => {
      if (!element) return undefined
      const bounds = element.getBoundingClientRect()
      return {
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
      }
    }
    const source = rewardElement?.getBoundingClientRect()
    const planned = planFlyers({
      source: source
        ? {
            x: source.left + source.width / 2,
            y: source.top + source.height / 2,
          }
        : { x: 0, y: 0 },
      coinTarget: toRect(coinBalanceElement),
      gemTarget: toRect(gemBalanceElement),
      delta: {
        coin: next.currency === 'coin' ? next.amount : 0,
        gem: next.currency === 'gem' ? next.amount : 0,
      },
    })
    landed = new Set()
    arrived = false
    interrupted = false
    setGrant(next)
    if (planned.length === 0) {
      // Nothing to animate (defensive) — deliver the credit immediately.
      arrived = true
      arrive()
      return
    }
    countFrom = input.balances()?.[next.currency] ?? 0
    countTarget = countFrom + next.amount
    // The count-up starts on the first landing (see finishFlyer) and spans
    // the landing window, so the number climbs while the burst arrives and
    // settles as the last coin lands. All flyers share one flight duration,
    // so the window is just the stagger between first and last departure.
    const delays = planned.map((flyer) => flyer.delayMs)
    countWindowMs = Math.max(...delays) - Math.min(...delays)
    setFlyers(planned)
  }

  const finishFlyer = (event: AnimationEvent) => {
    // Only per-flyer animationend events count; ignore the container's own.
    if (!event.target || event.target === event.currentTarget || arrived) return
    popPill()
    if (landed.size === 0 && !interrupted) {
      // The first coin has touched the pill: only now does the number start
      // rising (MONEY-1662). A straggler landing on an interrupted flight
      // must not start it — the credit will never land.
      cancelCount = animateCount({
        from: countFrom,
        delta: countTarget - countFrom,
        duration: countWindowMs,
        onTick: setCountedValue,
      })
    }
    landed.add(event.target)
    if (landed.size < flyers().length) return
    arrived = true
    // The last coin has landed: snap the count to the credited value and let
    // the settled number read for a beat before arrival is reported.
    cancelCount()
    setCountedValue(countTarget)
    settleTimer = window.setTimeout(arrive, ARRIVAL_SETTLE_MS)
  }

  const interruptFlight = (event: AnimationEvent) => {
    if (event.target === event.currentTarget || arrived) return
    // A cancelled flyer strands the flight: arrival never fires and no
    // fallback credit lands, so stop the cosmetic count and fall back to the
    // honest ledger value instead of displaying a number that was never
    // credited.
    interrupted = true
    cancelCount()
    setCountedValue(undefined)
  }

  return {
    coinPillRef: (element) => {
      coinBalanceElement = element
    },
    displayedBalance,
    finishFlyer,
    flyers,
    flying: () => grant() !== undefined,
    gemPillRef: (element) => {
      gemBalanceElement = element
    },
    grant,
    interruptFlight,
    launch,
    rewardAnchorRef: (element) => {
      rewardElement = element
    },
  }
}

/** The flight's burst: a full-viewport layer beside the reveal scaffold. */
export function CurrencyFlightLayer(props: {
  flight: CurrencyFlight
  testId: string
}) {
  const flight = () => props.flight
  const icon = () =>
    currencyPresentation[flight().grant()?.currency ?? 'coin'].icon
  return (
    <Show when={flight().flyers().length > 0}>
      <div
        data-testid={props.testId}
        aria-hidden="true"
        onAnimationEnd={(event) => flight().finishFlyer(event)}
        on:animationcancel={(event) => flight().interruptFlight(event)}
        class="pointer-events-none fixed inset-0 z-20"
      >
        <For each={flight().flyers()}>
          {(flyer) => (
            <img
              src={icon()}
              alt=""
              draggable={false}
              class="currency-reward-flyer absolute size-[22px] will-change-transform"
              style={{
                left: `${flyer.startX - 11}px`,
                top: `${flyer.startY - 11}px`,
                'animation-delay': `${flyer.delayMs}ms`,
                '--flyer-dx': `${flyer.endX - flyer.startX}px`,
                '--flyer-dy': `${flyer.endY - flyer.startY}px`,
              }}
            />
          )}
        </For>
      </div>
    </Show>
  )
}

/** A First-purchase bonus a purchase carried, as its Reward reveal presents it. */
export interface RevealedFirstPurchaseBonus {
  amount: number
  // The First-purchase bonus art; null presents the pack's own art.
  imageUrl: string | null
}

/**
 * A Shop currency product's Reward reveal: one grant, one Claim. A Time
 * Crystal purchase that carried a First-purchase bonus (GLOSSARY.md
 * "First-purchase bonus (首購翻倍)") presents as the bonus page instead: its
 * own title, the First-purchase bonus art (GLOSSARY.md "First-purchase bonus
 * art") — a complete image Shop overlays nothing on — and grant + bonus as
 * the amount Claim flies.
 */
export function CurrencyRewardReveal(props: {
  artworkStatus: Accessor<CurrencyArtworkStatus>
  balanceTestIdPrefix: string
  balances: Accessor<ShopBalances | undefined>
  // The First-purchase bonus the purchase carried; absent or null for the
  // ordinary reveal.
  firstPurchaseBonus?: RevealedFirstPurchaseBonus | null
  onArtworkError: () => void
  onArtworkLoad: () => void
  // Reports the claimed amount once it has landed; the caller credits it.
  onArrival: (amount: number) => void
  product: CurrencyProduct
  testIdPrefix: string
}) {
  const flight = createCurrencyFlight({
    balances: () => props.balances(),
    onArrival: (grant) => props.onArrival(grant.amount),
  })
  const firstPurchaseBonus = () => props.firstPurchaseBonus ?? null
  const amount = () => props.product.grant + (firstPurchaseBonus()?.amount ?? 0)
  // The bonus art's own load state: no other surface shows it. A failure is
  // final, as for currency product art.
  const [bonusArtStatus, setBonusArtStatus] =
    createSignal<RemoteArtworkStatus>('loading')
  const settleBonusArt = (status: 'failed' | 'loaded', src: string) => {
    if (bonusArtStatus() !== 'loading') return
    setBonusArtStatus(status)
    if (status === 'failed') {
      console.error('[shop] first-purchase bonus art failed', {
        product_id: props.product.productId,
        url: src,
      })
    }
  }
  // Claimed once: the flight readies itself again after arrival, but this
  // reveal has nothing more to present.
  const [claimed, setClaimed] = createSignal(false)
  const claim = () => {
    if (claimed()) return
    setClaimed(true)
    flight.launch({ amount: amount(), currency: props.product.currency })
  }

  return (
    <>
      <RewardRevealScaffold
        balanceTestIdPrefix={props.balanceTestIdPrefix}
        caption={currencyCaption(props.product.currency, amount())}
        coinPillRef={flight.coinPillRef}
        coinValue={flight.displayedBalance('coin')}
        ctaDisabled={claimed()}
        ctaLabel={m.claim()}
        ctaTestId={`${props.testIdPrefix}-claim`}
        gemPillRef={flight.gemPillRef}
        gemValue={flight.displayedBalance('gem')}
        onCta={claim}
        phase={claimed() ? 'flight' : 'ready'}
        rewardAnchorRef={flight.rewardAnchorRef}
        testId={`${props.testIdPrefix}-reveal`}
        title={
          firstPurchaseBonus() === null
            ? m.claimed_title()
            : m.reward_reveal_bonus_title()
        }
        variant={props.product.currency}
      >
        <Show
          when={firstPurchaseBonus()?.imageUrl}
          fallback={
            <CurrencyArtwork
              class="size-full"
              onError={props.onArtworkError}
              onLoad={props.onArtworkLoad}
              product={props.product}
              status={props.artworkStatus()}
              testId={`${props.testIdPrefix}-artwork`}
            />
          }
        >
          {(src) => (
            <RemoteArtwork
              class="size-full"
              onError={() => settleBonusArt('failed', src())}
              onLoad={() => settleBonusArt('loaded', src())}
              src={src()}
              status={bonusArtStatus()}
              testId={`${props.testIdPrefix}-artwork`}
            />
          )}
        </Show>
      </RewardRevealScaffold>

      <CurrencyFlightLayer
        flight={flight}
        testId={`${props.testIdPrefix}-flight`}
      />
    </>
  )
}
